import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigationType } from 'react-router-dom';
import { AppLayout } from '~/components/layout/AppLayout';
import { PatientReportPane } from '~/components/patientReports/PatientReportPane';
import Error404 from '../components/layout/Error404';
import { RouteLoader } from '../components/loaders/RouteLoader';
import { PreviousReportsPane } from '../components/patientReports/PreviousReportsPane';
import { PDFViewer } from '../components/PDFViewer';
import PatientDevicesPane from '~/components/patientReports/PatientDevicesPane';
import { getPatientPDFUrl } from '~/helpers';
import { usePatientHubQuery } from '~/api/patientReports/usePatientHubQuery';
import { CarePlan } from "~/components/carePlan/CarePlan";
import { logHubView } from '~/api/patientReports/logHubView';
const saveHubReview = async (patientId) => {
    await logHubView({ patientId });
};
const PatientHub = () => {
    const { patientId } = useParams();
    const navigationType = useNavigationType(); // For reload/back/forward detection
    const [downloadPDFFunction, setDownloadPDFFunction] = useState(null);
    const handleDownloadPDFReady = (downloadFn) => {
        setDownloadPDFFunction(() => downloadFn);
    };
    if (!patientId)
        throw new Error('Patient ID is required');
    useEffect(() => {
        saveHubReview(patientId);
    }, [patientId]);
    const [selectedPatient, setSelectedPatient] = useState();
    const [selectedReport, setSelectedReport] = useState();
    const [trendsSelected, setTrendsSelected] = useState();
    const [carePlanSelected, setCarePlanSelected] = useState();
    const [initialSensorReviewText, setInitialSensorReviewText] = useState('');
    const { isError, isSuccess, error, data, isFetchedAfterMount } = usePatientHubQuery(patientId);
    // --- Mark reloads before they happen (for manual refreshes)
    useEffect(() => {
        const handleBeforeUnload = () => {
            sessionStorage.setItem('wasReloaded', 'true');
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);
    // --- Initialize or create timer when patient changes ---
    useEffect(() => {
        if (!selectedPatient?.id)
            return;
        const storageKey = `patient-timer-${selectedPatient.id}`;
        const existingTimer = sessionStorage.getItem(storageKey);
        if (!existingTimer) {
            const timerData = {
                patientId: selectedPatient.id,
                startTime: Date.now(),
                totalPausedTime: 0,
                isRunning: true,
                lastPauseTime: null,
            };
            sessionStorage.setItem(storageKey, JSON.stringify(timerData));
        }
    }, [selectedPatient]);
    // --- The entry sanitizer + timer persistence logic ---
    useEffect(() => {
        if (!data)
            return;
        setSelectedPatient(data.patient[0]);
        setSelectedReport(data.patientReport[0]);
        const currentPatientId = data.patient[0].id;
        const storageKey = `patient-timer-${currentPatientId}`;
        // Detect real reloads more robustly
        const navEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
        const isReloadByPerf = navEntry?.type === 'reload';
        const wasReloadedFlag = sessionStorage.getItem('wasReloaded') === 'true';
        const isTrueReload = isReloadByPerf || wasReloadedFlag;
        // Clear reload flag after use
        sessionStorage.removeItem('wasReloaded');
        // --- If NOT a true reload, clear any leftover timer ---
        if (!isTrueReload) {
            console.log('Fresh entry (not reload) — clearing any existing timer');
            sessionStorage.removeItem(storageKey);
        }
        else {
            console.log('Detected reload — keeping existing timer');
        }
        // --- Re-initialize timer if missing ---
        if (!sessionStorage.getItem(storageKey)) {
            const timerData = {
                patientId: currentPatientId,
                startTime: Date.now(),
                totalPausedTime: 0,
                isRunning: true,
                lastPauseTime: null,
            };
            sessionStorage.setItem(storageKey, JSON.stringify(timerData));
        }
        // --- Initialize sensor review text ---
        if (isTrueReload) {
            setInitialSensorReviewText(data.pastNotes?.[0]?.notes ?? '');
        }
        else {
            setInitialSensorReviewText(data.patientReport?.[0]?.glucoseSensorReview ?? '');
        }
    }, [data, navigationType]);
    // --- Error / Loading handling ---
    if (isFetchedAfterMount === false)
        return React.createElement(RouteLoader, null);
    if (isError) {
        if (axios.isAxiosError(error) && error.response?.status === 404)
            return React.createElement(Error404, null);
        throw error;
    }
    if (isSuccess && data) {
        return (React.createElement(AppLayout, null,
            React.createElement("div", { className: "flex h-full relative" },
                React.createElement("div", { className: "flex flex-col flex-grow", style: { minWidth: 0 } },
                    React.createElement(PatientDevicesPane, { onDownloadPDF: downloadPDFFunction, trendsSelected: (selected) => setTrendsSelected(selected), carePlanSelected: (selected) => setCarePlanSelected(selected), tabSelected: (patient, report) => {
                            setSelectedPatient(patient);
                            setSelectedReport(patient.lastReport?.[0]);
                        }, patientArray: data.patient, patientReportArray: data.patientReport, patient: selectedPatient ?? data.patient[0], patientId: selectedPatient?.id ?? data.patient[0].id, patientReport: selectedReport ?? data.patientReport[0], pdfPath: getPatientPDFUrl(selectedReport?.path ?? data.patientReport[0]?.path ?? 'empty'), 
                        // pdfPath={getPatientPDFUrl('empty')}
                        cgmUrl: '', initialPrimaryPatient: data.patient[0], carePlanInitial: false }),
                    trendsSelected ? (React.createElement("div", { className: "bg-app-slate-charcoal flex-grow grid place-items-center overflow-auto" }, data.patientReport.length > 0 ? (React.createElement(PDFViewer, { file: selectedReport?.path ??
                            data.patientReport[0].path ??
                            'empty' })) : (React.createElement(PDFViewer, { file: 'empty' })))) : carePlanSelected ? (React.createElement(CarePlan, { patient: data.patient[0], onDownloadPDFReady: handleDownloadPDFReady })) : (React.createElement("div", { className: "bg-app-slate-charcoal flex-grow grid place-items-center overflow-auto" }, data.patientReport.length > 0 ? (React.createElement(PDFViewer, { file: selectedReport?.path ??
                            data.patientReport[0].path ??
                            'empty' })) : (React.createElement(PDFViewer, { file: 'empty' }))))),
                React.createElement("div", { className: "bg-app-gray-background overflow-auto p-5 relative flex-shrink-0 patient-report-pane pr-3", style: { width: '50%', scrollbarGutter: 'stable both-edges' } },
                    React.createElement(PatientReportPane, { key: initialSensorReviewText, patient: data.patient[0], patientReport: data.patientReport[0], primaryPatient: data.patient[0], didTalk: data.didTalk, timeSpentRpm: data.timeSpentRpm, timeSpentCcm: data.timeSpentCcm, notes: data.pastNotes, supervisingProviderName: data.supervisingProviderName, clinicSupervisingProviderName: data.clinicSupervisingProviderName, initialSensorReviewText: initialSensorReviewText })),
                React.createElement(PreviousReportsPane, { patientId: selectedPatient?.id ?? data.patient[0].id }))));
    }
    throw new Error('Invalid component state');
};
export default PatientHub;
